import {
  DnDNodeProps,
  AlertDialogProps,
  AlertFormProps,
  selectUser,
  useGetFolderQuery,
  useProjectDnDDefaultStyles,
} from '@centerpiece/components';
import { useItemColors } from '@centerpiece/components/src/utils/item.utils';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@mui/material';
import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useGetProjectContactsQuery,
  useGetProjectQuery,
  useGetItemsQuery,
  useGetProjectStagesQuery,
  useGetProjectStatesQuery,
  usePatchProjectMutation,
  useCreateProjectTemplateMutation,
  useCreateGroupingMutation,
  useCreateStageMutation,
  usePatchGroupingMutation,
  usePatchStageMutation,
  useUpdateItemMutation,
  useDeleteGroupingMutation,
  useCreateRelationshipMutation,
  useDeleteRelationshipMutation,
  useCreateTaskMutation,
  usePatchTaskMutation,
  usePatchItemMutation,
  useUpdateTaskMutation,
  useCreateEventMutation,
  usePatchEventMutation,
  useCreateCoordinationMutation,
  usePatchCoordinationMutation,
  useCreateProjectWorkflowMutation,
  useGetItemStatusesQuery,
  useGetSubscribersQuery,
  useGetTagsQuery,
  useGetSubscriptionContactsQuery,
  useGetCommentsQuery,
  useGetRelationshipsQuery,
  useGetRolesQuery,
  useGetContactsQuery,
} from 'store/api';
import {
  Comment,
  CommentContact,
  Contact,
  Item,
  ItemStatus,
  MessageContact,
  NodeItem,
  Project,
  ProjectContact,
  ProjectStage,
  ProjectState,
  Quote,
  Relationship,
  Role,
  Stage,
  Subscriber,
  Tag,
} from 'store/api/models';
import { useLogout } from 'utils/logout.utils';
import { getOptimizedProjectTree } from 'utils/project.utils';
export type ProjectContextContent = {
  id: number;
  project: Project;
  projectColors: any;
  projectLoading: boolean;
  preview: boolean;
  template: boolean;
  disabled: boolean;
  items: Item[];
  itemsFetching: boolean;
  comments: Comment[];
  commentsLoading: boolean;
  tags: Tag[];
  project_contacts: ProjectContact[];
  msgs: any[];
  cmts: any[];
  projectTree: DnDNodeProps;
  events: any[];
  stages: any[];
  project_stages: ProjectStage[];
  project_states: ProjectState[];
  refetchProject: Function;
  currentStage: Stage;
  nextStage: Stage;
  statuses: ItemStatus[];
  subscribers: Subscriber[];
  afterSave: Function;
  patchProject: Function;
  itemsRef: any;
  completeChildren: Function;
  selectedQuote?: Quote;
  setSelectedQuote: React.Dispatch<React.SetStateAction<Quote | undefined>>;
  role_relationships: Relationship[];
  roles: Role[];
  alertDetails: AlertDialogProps;
  setAlertDetails: React.Dispatch<React.SetStateAction<AlertDialogProps>>;
  contacts: { [key: number]: Contact };
  activeNode?: DnDNodeProps;
  setActiveNode: (node: DnDNodeProps | undefined) => void;
};
export const ProjectContext = createContext<ProjectContextContent>({
  id: 0,
  project: {} as Project,
  projectColors: null,
  projectLoading: false,
  preview: false,
  template: false,
  disabled: false,
  items: [],
  itemsFetching: false,
  comments: [],
  commentsLoading: false,
  tags: [],
  project_contacts: [],
  msgs: [],
  cmts: [],
  projectTree: {} as DnDNodeProps,
  events: [],
  stages: [],
  project_stages: [],
  project_states: [],
  refetchProject: () => {},
  currentStage: {} as Stage,
  nextStage: {} as Stage,
  statuses: [],
  subscribers: [],
  afterSave: () => {},
  patchProject: () => {},
  itemsRef: null,
  completeChildren: () => {},
  setSelectedQuote: () => {},
  role_relationships: [],
  roles: [],
  alertDetails: {} as AlertDialogProps,
  setAlertDetails: () => {},
  contacts: [],
  setActiveNode: () => {},
});

export const ProjectProvider = ({
  id,
  exampleProject,
  preview = false,
  template = false,
  disabled = false,
  children,
  afterSave = () => {},
  itemsRef,
}: {
  id: number;
  exampleProject?: any;
  preview?: boolean;
  template?: boolean;
  disabled?: boolean;
  children: any;
  afterSave?: Function;
  itemsRef?: any;
}) => {
  const { data: contactsRaw = [] } = useGetContactsQuery({});
  const contacts = useMemo(() => {
    let contacts: { [key: number]: Contact } = {};
    contactsRaw.forEach((c: Contact) => {
      contacts[c.id] = c;
    });
    return contacts;
  }, [contactsRaw]);
  const { data: project_contacts = [], isLoading: projectContactsLoading } =
    useGetProjectContactsQuery(
      { params: { nested: 'all', project: id, type: 'Internal' } },
      { skip: !id || !!exampleProject }
    );
  const [anchorEl, setAnchorElActual] =
    useState<null | {
      anchor: HTMLElement;
      clientX: any;
      clientY: any;
      data?: any;
    }>(null);
  const setAnchorEl = (e: any) => {
    if (!e) {
      setHighlightNode(undefined);
    }
    setAnchorElActual(e);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const onLogout = useLogout();
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('Active');
  const [activeNode, setActiveNodeActual] = useState<DnDNodeProps>();
  const setActiveNode = (node: DnDNodeProps | undefined) => {
    if (!disabled) {
      setActiveNodeActual(node);
    }
  };
  const [itemType, setItemType] = useState<{
    label: string;
    filter: string;
  }>({
    label: 'All',
    filter: 'All',
  });
  const [selectedTag, setSelectedTag] = useState<{
    name: string;
    id: number;
  }>({
    name: 'All',
    id: -1,
  });
  const [activeDialog, setActiveDialogActual] = useState<DnDNodeProps>();
  const setActiveDialog = (node: DnDNodeProps | undefined) => {
    if (!disabled) {
      setActiveDialogActual(node);
    }
  };
  const [highlightNode, setHighlightNode] = useState<DnDNodeProps>();
  const [openProjectInfo, setOpenProjectInfo] = useState<string>();
  const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDialogProps>({
    open: false,
  });
  const [alertFormDetails, setAlertFormDetails] = useState<AlertFormProps>({
    open: false,
  });
  const [selectedQuote, setSelectedQuote] = useState<Quote>();
  const handleOpenSpeedDial = () => setOpenSpeedDial(true);
  const handleCloseSpeedDial = () => setOpenSpeedDial(false);
  // eslint-disable-next-line
  const { contact: userContact } = useSelector(selectUser);
  const cacheBust = useRef(Date.now()).current;
  const {
    currentData: project = exampleProject?.project || {},
    isLoading: projectLoading,
    refetch: actualRefetchProject,
  } = useGetProjectQuery(
    filter === 'Archived'
      ? {
          id: Number(id) || 0,
          params: {
            archived: true,
            optimized: true,
            cachBust: cacheBust,
          },
        }
      : {
          id: Number(id) || 0,
          params: { optimized: true, cachBust: cacheBust },
        },
    { skip: !id, refetchOnMountOrArgChange: true }
  );

  const {
    data: items = exampleProject?.items || [],
    isFetching: itemsFetching,
    refetch: refetchItems,
  } = useGetItemsQuery(
    {
      params: {
        optimized: 'project',
        project: id,
        archived: filter === 'Archived' ? true : undefined,
        cachBust: cacheBust,
      },
    },
    { skip: !id || !!exampleProject, refetchOnMountOrArgChange: true }
  );

  const {
    data: msgs = [] as MessageContact[],
    isFetching: msgsFetching,
    refetch: refetchMsgs,
  } = useGetItemsQuery(
    {
      params: {
        optimized: 'msgs',
        project: id,
        archived: filter === 'Archived' ? true : undefined,
        cachBust: cacheBust,
      },
    },
    { skip: !id || !!exampleProject, refetchOnMountOrArgChange: true }
  );
  const {
    data: cmts = [] as CommentContact[],
    isFetching: cmtsFetching,
    refetch: refetchCmts,
  } = useGetItemsQuery(
    {
      params: {
        optimized: 'cmts',
        project: id,
        archived: filter === 'Archived' ? true : undefined,
        cachBust: cacheBust,
      },
    },
    { skip: !id || !!exampleProject, refetchOnMountOrArgChange: true }
  );
  const { data: roles = [] } = useGetRolesQuery({});
  const { data: role_relationships = [], refetch: refetchRelationships } =
    useGetRelationshipsQuery({
      params: { nested: 'role', project: id },
    });
  const refetchProject = async () => {
    await Promise.all([
      actualRefetchProject(),
      refetchItems(),
      refetchRelationships(),
      refetchMsgs(),
      refetchCmts(),
    ]);
    afterSave();
  };

  const { data: project_stages = [] } = useGetProjectStagesQuery({
    params: { nested: 'all' },
  });
  const { data: project_states = [] } = useGetProjectStatesQuery({
    params: { nested: 'all' },
  });
  const [patchProject] = usePatchProjectMutation();
  useGetFolderQuery(
    { project_id: Number(id), folder_path: '/' },
    { refetchOnMountOrArgChange: true, skip: !id }
  );
  const [createProjectTemplate] = useCreateProjectTemplateMutation();
  const [createGrouping] = useCreateGroupingMutation();
  const [createStage] = useCreateStageMutation();
  const [patchGrouping, patchGroupingResult] = usePatchGroupingMutation();
  const [patchStage, patchStageResult] = usePatchStageMutation();
  const [updateItem] = useUpdateItemMutation();
  const [deleteGrouping] = useDeleteGroupingMutation();
  const [createRelationship] = useCreateRelationshipMutation();
  const [deleteRelationship] = useDeleteRelationshipMutation();
  const [createTask] = useCreateTaskMutation();
  const [patchTask, patchTaskResult] = usePatchTaskMutation();
  const [patchItem, patchItemResult] = usePatchItemMutation();
  const [updateTask, updateTaskResult] = useUpdateTaskMutation();
  const [createEvent] = useCreateEventMutation();
  const [patchEvent, patchEventResult] = usePatchEventMutation();
  const [createCoordination] = useCreateCoordinationMutation();
  const [patchCoordination, patchCoordinationResult] =
    usePatchCoordinationMutation();
  const [createProjectWorkflow] = useCreateProjectWorkflowMutation();
  const { data: statuses = [] } = useGetItemStatusesQuery({});
  const { data: subscribers = [] } = useGetSubscribersQuery({
    params: { nested: 'all' },
  });
  const { data: tags = [] } = useGetTagsQuery({});
  const projectDnDStyles = useProjectDnDDefaultStyles();

  const { data: comments = [], isLoading: commentsLoading } =
    useGetCommentsQuery(
      { params: { nested: 'all', project: id } },
      { skip: !id }
    );
  const numChats = comments?.filter(
    (comment: Comment) =>
      comment?.contacts?.filter(
        (contact: any) =>
          contact.status === 'New' && contact.contact === userContact
      )?.length > 0
  )?.length;

  const { root: projectTree, events } = useMemo(() => {
    return getOptimizedProjectTree({
      project: { ...project, contents: items as any, msgs, cmts },
      setActiveNode: setActiveNode,
      activeNode: activeNode,
    });
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // if we track activeNode there will be infinite re-renders
    // as we need to update the activeNode data upon project api refresh

    // eslint-disable-next-line
  }, [project, items, msgs, cmts]);
  const stages = projectTree?.data?.stages;
  const currentStage: Stage = stages?.filter(
    (s: any) => !s?.stage?.outcome
  )?.[0]?.stage;
  const nextStage: Stage = stages?.filter((s: any) => !s?.stage?.outcome)?.[1]
    ?.stage;
  const completeChildren = async (children?: NodeItem[]) => {
    await children?.forEach(async child => {
      await completeChildren(child.items);
      if (!child.complete) {
        if (child.coordination) {
          await patchCoordination({
            id: child.coordination.id,
            status: 3,
          } as any);
        } else if (child.event) {
          await patchEvent({ id: child.event.id, status: 3 } as any);
        } else if (child.task) {
          await patchTask({ id: child.task.id, status: 3 } as any);
        }
      }
    });
  };

  const projectColors = useItemColors({
    date: project?.due_date,
    complete: project.state === 1,
  });
  return (
    <ProjectContext.Provider
      value={{
        id,
        project,
        projectColors,
        projectLoading,
        preview,
        template,
        disabled,
        items,
        itemsFetching,
        comments,
        commentsLoading,
        tags,
        project_contacts,
        msgs,
        cmts,
        projectTree,
        events,
        stages,
        project_states,
        project_stages,
        refetchProject,
        currentStage,
        nextStage,
        statuses,
        subscribers,
        afterSave,
        patchProject,
        itemsRef,
        completeChildren,
        selectedQuote,
        setSelectedQuote,
        role_relationships,
        roles,
        alertDetails,
        setAlertDetails,
        contacts,
        activeNode,
        setActiveNode,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => useContext(ProjectContext);
